import React, { useState, useEffect, useMemo } from 'react';
import dateFormat from 'dateformat';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { EditOutlinedIcon, BinOutlinedIcon } from '../Icons';
import classes from './cvBaseItem.module.scss';
import DeleteDialog from '../ModalWindows/DeleteDialog';
import AddEditCVRecord from '../ModalWindows/AddEditCVRecord';
import { isValidDate } from '../../../utils/helpers';
import { deleteModalConfigs } from '../../../utils/constants';
import { getInitialValues, getValidationSchema } from '../ModalWindows/helpers/addEditForm';
import ValidationErrorLabel from '../ValidationErrorLabel/ValidationErrorLabel';
import ActionsButton from '../buttons/ActionsButton/ActionsButton';
import { usePrevious } from '../../../utils/hooks';

function CVBaseItem(props) {
  const {
    item,
    deleteItem,
    editItem,
    block,
    showDescription = true,
    withCurrentlyCheckBox,
    extra = {},
    shouldImmediatelySave = false,
    pending = false
  } = props;

  const [open, setOpen] = useState(false);
  const [openAddWindow, setAddWindow] = useState(false);
  const prevPending = usePrevious(pending);
  const { t } = useTranslation();
  const deleteModalConfig = useMemo(() => deleteModalConfigs(t), [t]);
  const blocks = {
    works: {
      title2: item?.Title || '-',
      description: item?.Description || '-',
      title1: item?.CompanyName || '-',
      startDate: item?.StartDate,
      endDate: item?.CurrentlyWorkThere && withCurrentlyCheckBox ? new Date() : item?.EndDate,
      currentlyWorkThere: item?.CurrentlyWorkThere
    },
    trainings: {
      title2: item?.CertificationId,
      title1: item?.CertificationType,
      validUntil: item?.ValidUntil
    },
    educations: {
      title1: item?.UniversityName,
      title2: `${item?.Degree || '-'} , ${item?.FieldOfStudy || '-'}`,
      startDate: item?.StartDate,
      endDate: item?.CurrentlyStudyHere && withCurrentlyCheckBox ? new Date() : item?.EndDate
    }
  };

  const info = {
    title1: blocks[block].title1,
    title2: blocks[block].title2,
    description: blocks[block]?.description,
    startDate: blocks[block]?.startDate,
    endDate: blocks[block]?.endDate,
    currentlyWorkThere: blocks[block]?.currentlyWorkThere,
    validUntil: blocks[block]?.validUntil
  };

  const confirmDeleting = async () => {
    await deleteItem();
    // setOpen(false);
  };

  const confirmHandler = async (data) => {
    await editItem(data);
    if (!withCurrentlyCheckBox) setAddWindow(false);
  };

  const dateDesign = {
    works: `${isValidDate(new Date(info.startDate)) ? dateFormat(info.startDate, 'dd mmm yyyy') : '-'} - 
                ${isValidDate(new Date(info.endDate)) ? dateFormat(info.endDate, 'dd mmm yyyy') : '-'}`,
    trainings: `${t('dashboard.jobs.validto')} ${info.validUntil ? dateFormat(info.validUntil, 'dd mmm yyyy') : ' - '}`,
    educations: `${isValidDate(new Date(info.startDate)) ? dateFormat(info.startDate, 'mmm yyyy') : '-'} - 
                ${isValidDate(new Date(info.endDate)) ? dateFormat(info.endDate, 'mmm yyyy') : '-'}`
  };

  const itemForValidation = useFormik({
    initialValues: getInitialValues(true, item, block, t),
    validationSchema: getValidationSchema(block, item, t),
    initialTouched: Object.keys(getInitialValues(true, item, block, t)).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {}),
    enableReinitialize: true
  });

  useEffect(() => {
    itemForValidation.validateForm();
  }, []);

  useEffect(() => {
    if (prevPending === true && pending === false) {
      setAddWindow(false);
    }
  }, [pending]);

  return (
    <div data-testid="CVBaseItem" className={classes.mainSection}>
      {openAddWindow ? (
        <div className={classes.editing}>
          <AddEditCVRecord
            withCurrentlyCheckBox={withCurrentlyCheckBox}
            open
            closeHandler={() => setAddWindow(false)}
            editeMode
            initData={item}
            confirmHandler={confirmHandler}
            deleteHandler={deleteItem}
            block={block}
            extra={extra}
            shouldImmediatelySave={shouldImmediatelySave}
            pending={pending}
          />
        </div>
      ) : (
        <div className={classes.info}>
          {(itemForValidation.errors?.company ||
            itemForValidation.errors?.school ||
            itemForValidation.errors?.type) && (
            <ValidationErrorLabel
              message={
                itemForValidation.errors?.company || itemForValidation.errors?.school || itemForValidation.errors?.type
              }
            />
          )}
          <span className={classes.company}>{info.title1}</span>
          {(itemForValidation.errors?.title ||
            itemForValidation.errors?.degree ||
            itemForValidation.errors?.fieldofstudy ||
            itemForValidation.errors?.certId) && (
            <ValidationErrorLabel
              message={
                itemForValidation.errors?.title ||
                itemForValidation.errors?.degree ||
                itemForValidation.errors?.fieldofstudy ||
                itemForValidation.errors?.certId
              }
            />
          )}
          <span className={classes.position}>{block !== 'trainings' ? info?.title2 : `ID${info?.title2}`}</span>
          {(itemForValidation.errors?.startdate ||
            itemForValidation.errors?.enddate ||
            itemForValidation.errors?.valid) && (
            <ValidationErrorLabel
              message={
                itemForValidation.errors?.startdate ||
                itemForValidation.errors?.enddate ||
                itemForValidation.errors?.valid
              }
            />
          )}
          <span className={classes.dates}>{dateDesign[block]}</span>
        </div>
      )}
      {!openAddWindow && (
        <div className={classes.actions}>
          <ActionsButton activityName="edit" clickHandler={() => setAddWindow(true)} />
          <ActionsButton activityName="delete" clickHandler={() => setOpen(true)} />
          <DeleteDialog
            open={open}
            closeHandler={() => setOpen(false)}
            confirmHandler={confirmDeleting}
            config={deleteModalConfig[block]}
            pending={pending}
          />
        </div>
      )}
      {block === 'works' && showDescription && !openAddWindow && (
        <div className={classes.description}>
          {itemForValidation.errors?.description && (
            <ValidationErrorLabel message={itemForValidation.errors?.description} />
          )}
          {info.description}
        </div>
      )}
    </div>
  );
}

export default CVBaseItem;
